import { useEffect, useRef, useState } from 'react';
import { useOrgStore } from '@/stores/org-store';
import { extractOrgFromPath } from '@/utils/org-path';
import { resolveOrgSwitch } from '@/utils/org-switch';
import { takeResumeLocation } from '@/utils/resume-location';

/**
 * The page an involuntary sign-out left behind, ready to navigate to, or null
 * when there is nothing to resume. `undefined` while it is still being
 * resolved, so callers can hold their own redirect until the answer is in.
 */
export function useResumeLocation() {
  const { orgs, currentOrg } = useOrgStore();
  const [target, setTarget] = useState<string | null | undefined>(undefined);
  const hasResolvedRef = useRef(false);

  useEffect(() => {
    if (hasResolvedRef.current || !currentOrg) return;
    hasResolvedRef.current = true;

    const resume = takeResumeLocation();
    if (!resume) {
      setTarget(null);
      return;
    }

    // Sign-out clears the stored org, so the org resolved on the way back in is
    // often not the one the remembered page belongs to. Only the reachability
    // question is answered here — OrgSyncGuard performs the switch itself, so
    // the switch keeps the full reload it does to avoid leaking the previous
    // org's data. An unreachable org resumes nowhere rather than landing the
    // user on the guard's error screen.
    const resumeOrgId = extractOrgFromPath(resume.split(/[?#]/)[0])?.orgId;
    const resolution = resolveOrgSwitch(orgs, currentOrg, resumeOrgId);
    const isReachable =
      resolution.status === 'synced' || resolution.status === 'switch';

    setTarget(isReachable ? resume : null);
  }, [orgs, currentOrg]);

  return target;
}
